import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { ChatBubbleLeftRightIcon, ClipboardDocumentIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import api, { getErrorMessage } from '../../../services/api';

/**
 * «Consultar el caso»: la segunda opinión de la IA sobre el expediente de una
 * propuesta, con el expediente entero delante. Ver docs/consultar-el-caso-plan.md.
 *
 * La pide cualquiera que revise: el admin (también en lo AR que solo supervisa)
 * y la empresa o su gestor en /ar-review. El servidor decide qué ve cada uno.
 *
 * Va en segundo plano: el POST vuelve enseguida (Heroku corta a los 30 s) y aquí
 * se pregunta cada pocos segundos hasta que está.
 */
const POLL_MS = 4000;

const CONFIANZA = { high: 'alta', medium: 'media', low: 'baja' };
const CANAL = { email: 'correo', whatsapp: 'WhatsApp', sms: 'SMS' };

function fechaHora(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleString('es-ES', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function Bloque({ titulo, children }) {
  return (
    <div>
      <p className='text-xs font-semibold uppercase tracking-wide text-slate-500'>{titulo}</p>
      <div className='mt-1 text-sm text-slate-800 whitespace-pre-line'>{children}</div>
    </div>
  );
}

function Valoracion({ consulta }) {
  const o = consulta.output || {};
  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(o.draft.text);
      toast.success('Borrador copiado');
    } catch {
      toast.error('No se pudo copiar');
    }
  };
  return (
    <div className='space-y-3'>
      {o.situation && <Bloque titulo={consulta.format === 'free_text' ? 'Valoración' : 'Situación'}>{o.situation}</Bloque>}
      {o.debtor_position && <Bloque titulo='Qué alega el deudor'>{o.debtor_position}</Bloque>}
      {o.risks?.length > 0 && (
        <Bloque titulo='Riesgos'>
          <ul className='list-disc pl-5 space-y-0.5'>
            {o.risks.map((r, i) => (
              <li key={i}>{r}</li>
            ))}
          </ul>
        </Bloque>
      )}
      {o.recommendation && <Bloque titulo='Qué haría'>{o.recommendation}</Bloque>}
      {o.missing && <Bloque titulo='Falta saber'>{o.missing}</Bloque>}
      {o.draft?.text && (
        <div className='rounded-lg border border-slate-200 bg-slate-50 p-3'>
          <div className='flex items-center justify-between gap-2'>
            <p className='text-xs font-semibold uppercase tracking-wide text-slate-500'>
              Borrador por {CANAL[o.draft.channel] || o.draft.channel}
            </p>
            <button
              type='button'
              onClick={copiar}
              className='inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs text-indigo-700 hover:bg-indigo-50'
            >
              <ClipboardDocumentIcon className='h-4 w-4' /> Copiar borrador
            </button>
          </div>
          <p className='mt-2 text-sm text-slate-800 whitespace-pre-line'>{o.draft.text}</p>
        </div>
      )}
      <p className='text-xs text-slate-500'>
        {o.confidence && <>Confianza {CONFIANZA[o.confidence] || o.confidence} · </>}
        Es una opinión orientativa de la IA, no asesoramiento jurídico: decide tú.
      </p>
    </div>
  );
}

const AIAgentConsult = ({ planId, basePath = '/ai-agent' }) => {
  const [consulta, setConsulta] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [pidiendo, setPidiendo] = useState(false);
  const timer = useRef(null);

  const parar = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
  };

  const seguir = useCallback(
    (id) => {
      parar();
      timer.current = setTimeout(async () => {
        try {
          const { data } = await api.get(`${basePath}/consults/${id}`);
          setConsulta(data.data);
          if (data.data?.status === 'running') seguir(id);
        } catch (err) {
          toast.error(getErrorMessage(err));
        }
      }, POLL_MS);
    },
    [basePath]
  );

  // Al abrir el caso, la última consulta que haya, para no pagarla otra vez.
  useEffect(() => {
    let vivo = true;
    setCargando(true);
    setConsulta(null);
    (async () => {
      try {
        const res = await api.get(`${basePath}/plans/${planId}/consults`);
        const ultima = res?.data?.data?.[0] || null;
        if (!vivo) return;
        setConsulta(ultima);
        if (ultima?.status === 'running') seguir(ultima._id);
      } catch {
        // Sin historial se puede consultar igual: el botón sigue ahí.
      } finally {
        if (vivo) setCargando(false);
      }
    })();
    return () => {
      vivo = false;
      parar();
    };
  }, [planId, basePath, seguir]);

  const consultar = async () => {
    setPidiendo(true);
    try {
      const { data } = await api.post(`${basePath}/plans/${planId}/consult`);
      setConsulta(data.data);
      if (data.data?.status === 'running') seguir(data.data._id);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setPidiendo(false);
    }
  };

  const enMarcha = consulta?.status === 'running';

  return (
    <div className='rounded-lg border border-violet-200 bg-white p-4 space-y-3'>
      <div className='flex items-start justify-between gap-3 flex-wrap'>
        <div>
          <p className='font-medium text-slate-800 flex items-center gap-2'>
            <ChatBubbleLeftRightIcon className='h-5 w-5 text-violet-600' /> Consultar el caso
          </p>
          <p className='text-xs text-slate-500 mt-0.5'>
            Segunda opinión de la IA con el expediente entero: qué alega el deudor, riesgos y qué haría.
          </p>
        </div>
        {!cargando && !enMarcha && (
          <button
            type='button'
            disabled={pidiendo}
            onClick={consultar}
            className='inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-violet-300 text-sm font-medium text-violet-700 hover:bg-violet-50 disabled:opacity-50'
          >
            {consulta ? <ArrowPathIcon className='h-4 w-4' /> : <ChatBubbleLeftRightIcon className='h-4 w-4' />}
            {consulta ? 'Consultar de nuevo' : 'Consultar el caso'}
          </button>
        )}
      </div>

      {enMarcha && (
        <p className='text-sm text-slate-600' role='status'>
          Analizando el expediente… suele tardar un minuto. Puedes seguir revisando otros casos.
        </p>
      )}

      {consulta?.status === 'failed' && (
        <p className='text-sm text-red-700'>No se pudo completar la consulta: {consulta.error || 'error desconocido'}.</p>
      )}

      {consulta?.status === 'ready' && (
        <>
          <p className='text-xs text-slate-400'>Consultado el {fechaHora(consulta.completed_at || consulta.created_at)}</p>
          <Valoracion consulta={consulta} />
        </>
      )}
    </div>
  );
};

export default AIAgentConsult;
